// TabContent1.js
import React, { useEffect, useState } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import SelectGroup from '../../../../theme/Select/SelectGroup';
import { ReactComponent as ShopifyLogo } from '../../../../../styles/svg/shopify.svg';
import { ReactComponent as WooCommerceLogo } from '../../../../../styles/svg/woocommerce.svg';
import { ReactComponent as BolLogo } from '../../../../../styles/svg/bol.svg';
import { ReactComponent as CcvshopLogo } from '../../../../../styles/svg/ccvshop.svg';
import { ReactComponent as KauflandLogo } from '../../../../../styles/svg/kaufland.svg';
import { ReactComponent as LightspeedLogo } from '../../../../../styles/svg/lightspeed.svg';
import { ReactComponent as MagentoLogo } from '../../../../../styles/svg/magento.svg';
import { ReactComponent as StockhubLogo } from '../../../../../styles/svg/pin.svg';
import { Box, useTheme } from '@mui/material';
import { getWebshopLogo } from '../../../../../utils/logomapper';
import { useTranslation } from 'react-i18next';
import Subheading from '../../../../theme/text/Subheading';
import Text from '../../../../theme/text/Text';
import { useGetSettingsQuery } from '../../../../../newapi/global/generalApiSlice';
import { tokens } from '../../../../../theme';

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const { t } = useTranslation();
        const theme = useTheme();
        const colors = tokens(theme.palette.mode);

    const [hasAmazonKey, setHasAmazonKey] = useState(false);
    const { data: amazonKeys } = useGetSettingsQuery("amazon_enabled");
    useEffect(() => {
        if (amazonKeys && amazonKeys.length > 0) {
            const setting = amazonKeys[0];
            if (setting.key === "amazon_enabled") {
                setHasAmazonKey(setting.value === 'true');
            }
        }
    }, [amazonKeys]);

    const [hasTikTokKey, setHasTikTokKey] = useState(false);
    const { data: tiktokKeys } = useGetSettingsQuery("tiktok_enabled");
    useEffect(() => {
        if (tiktokKeys && tiktokKeys.length > 0) {
            const setting = tiktokKeys[0];
            if (setting.key === "tiktok_enabled") {
                setHasTikTokKey(setting.value === 'true');
            }
        }
    }, [tiktokKeys]);

    const columns = [
        { title: "Shopify", key: "shopify" },
        { title: "Woocommerce", key: "woocommerce" },
        { title: "Bol.com", key: "bol" },
        { title: "CCV Shop", key: "ccvshop" },
        { title: "Kaufland", key: "kaufland" },
        { title: "Lightspeed eCommerce", key: "lightspeed_ecom" },
        { title: "Magento", key: "magento" },
        { title: "Mirakl", key: "mirakl" },
        { title: "Plug&Pay", key: "plugandpay" },
        { title: "PrestaShop", key: "prestashop" },
        { title: "Shopware", key: "shopware6" },
        // Direct Amazon channel — only offered when the tenant has it enabled.
        ...(hasAmazonKey ? [{ title: "Amazon", key: "amazon_stockhub" }] : []),
        // TikTok Shop channel — only offered when the tenant has it enabled.
        ...(hasTikTokKey ? [{ title: "TikTok Shop", key: "tiktokshop" }] : []),
        // { title: "Marketplaces", key: "etailize" },
        { title: "API", key: "api" },
    ].map(column => ({
        ...column,
        logo: getWebshopLogo(column.key,{theme,colors})
    }));

    const marketPlaces = [
        { title: t("marketplaces"), key: "etailize", extra:<Text fontSize={13} sx={{ml:"auto"}} light>Powered by e-tailize</Text> }
    ].map(column => ({
        ...column,
        logo: getWebshopLogo(column.key,{theme,colors})
    }));

    const handleOptionSelect = (option) => {
        const webshopType = columns[option].key;
        
        // Standard webshop types continue with normal flow
        const credentialsObj = getCredentialsFromType(webshopType);
        props.handleDataUpdate({ 
            credentials: credentialsObj,
            flow_type: "standard"
        });
        props.handleDataUpdate({ selectedOption: option, webshop_type: webshopType });
        props.handleNext("tabCredentials");
    };

    const handleMarketPlaceSelect = (option) => {
        const webshopType = marketPlaces[option].key;
        
        if (webshopType === "etailize") {
            // Route to etailize flow instead of standard flow
            props.handleDataUpdate({ 
                selectedOption: option, 
                webshop_type: webshopType,
                flow_type: "etailize"
            });
            props.handleNext("tabEtailizeMarketplace");
            return;
        }
        
        // Standard webshop types continue with normal flow
        const credentialsObj = getCredentialsFromType(webshopType);
        props.handleDataUpdate({ 
            credentials: credentialsObj,
            flow_type: "standard"
        });
        props.handleDataUpdate({ selectedOption: option, webshop_type: webshopType });
        props.handleNext("tabCredentials");
    };


        const getCredentialsFromType = (type, platform = null) => {
            switch(type) {
                case "woocommerce":
                    return {url: "", consumer_key: "", consumer_secret: ""};
                case "shopify":
                    return {};
                case "bol":
                    return {};
                case "ccvshop":
                    return {url: "", public_key: "", secret_key: ""};
                case "kaufland":
                    return {client_key: "", client_secret: "", storefront: ""};
                case "magento":
                    return {shop: "", access_token: ""};
                case "lightspeed_ecom":
                    return {api_key: "", api_secret: ""};
                case "amazon_stockhub":
                    return {
                        refresh_token: "",
                        selling_partner_id: "",
                        marketplace: "NL",
                        marketplace_id: "A1805IZSGTT6HS",
                    };
                case "tiktokshop":
                    return {
                        access_token: "",
                        refresh_token: "",
                        expiresAt: "",
                        shop_cipher: "",
                        shop_id: "",
                        seller_name: "",
                        region: "global",
                    };
                case "etailize":
                    if (platform) {
                        // Initialize credentials based on platform requirements
                        const credentials = {
                            platform: platform.id,
                            syncOrders: true,
                            syncStock: true
                        };

                        // Add required fields from platform definition
                        if (platform.required_fields) {
                            platform.required_fields.forEach(field => {
                                credentials[field] = "";
                            });
                        }

                        return credentials;
                    }
                    return {platform: "", api_key: "", api_secret: ""};
                case "mirakl":
                    return {url: "", api_key: "", shop_id: ""};
                case "plugandpay":
                    return {access_token: ""};
                case "prestashop":
                    return {access_token: "", shop: ""};
                case "shopware6":
                    return {url: "", client_id: "", client_secret: ""};
                case "api":
                    return {};
                default:
                    return {};
            }
        }

        const [hasEtailizeKey, setHasEtailizeKey] = useState(false);
        // const { data: etailizeKeys} = useGetSettingsQuery("etailize_enabled");
        // useEffect(() => {
        //     if(etailizeKeys && etailizeKeys.length > 0){
        //          const setting = etailizeKeys[0];
        //         if(setting.key === "etailize_enabled"){
        //             if(setting.value === 'true'){
        //                 setHasEtailizeKey(true);
        //             } else {
        //                 setHasEtailizeKey(false)
        //             }
        //         }

        //     }

        // },[etailizeKeys])

    
        // Default webshop type selection
        return (
            <Box>
                <SelectGroup 
                    selected={-1} 
                    setSelected={handleOptionSelect} 
                    options={columns} 
                    image={true} 
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "16px",
                        gridAutoRows: "auto",
                        "@media screen and (max-width: 47.9375em)" : {
                            gridTemplateColumns: "repeat(2, 1fr)",

                        },
                        option: {
                            flexDirection:"row",
                            alignItems:"center",
                            gap:2,
                            width:1,
                        }
                    }}
                />

                {/* {hasEtailizeKey && (<>
                <Subheading sx={{mt:4,mb:1,}}>{t("marketplaces")}</Subheading>
                <SelectGroup 
                    selected={-1} 
                    setSelected={handleMarketPlaceSelect} 
                    options={marketPlaces} 
                    image={true} 
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(1, 1fr)",
                        gap: "16px",
                        gridAutoRows: "auto",
                        "@media screen and (max-width: 47.9375em)" : {
                            gridTemplateColumns: "repeat(1, 1fr)",

                        },

                        option: {
                            flexDirection:"row",
                            alignItems:"center",
                            gap:2,
                            width:1,
                        }
                    }}
                />
                </>)} */}
            </Box>
        );
    };

const ChooseWebshopType = (props) => {
    return (
        <ProgressionTab 
            tabId="tabChooseType" 
            step={1} 
            maxSteps={3}
            sx={{
                body: {
                    maxHeight:550,
                }
            }}
        >
            <Content/>
        </ProgressionTab>
    )
}

export default ChooseWebshopType;